import {type UIGameCardController} from './ui_game_card/UIGameCardController.ts'
import {type UIGameCanvasController} from './UIGameCanvasController.ts'
import {type UIGameMapController} from './ui_game_map/UIGameMapController.ts'
import {type UIGameActionsController} from './ui_game_actions/UIGameActionsController.ts'
import {type UIGameToolTipController} from './ui_game_tooltip/UIGameToolTipController.ts'
import {UIGameEventController} from './UIGameEventController.ts'
import {type GameClientState} from '../../../../shared/GameClientState.ts'
import {type BuildGameConfigurations, ModeSettingEnum} from '@colonist/game-base-types'
import {SocketGameSend} from '../socket/SocketGameSend.ts'
import {type UIAdController} from '../ui_ad/UIAdController.ts'
import {socket} from '../EntryShared.ts'
import {type UIGamePauseController} from './UIGamePauseController.ts'
import {CA} from '../analytics/Analytics.ts'
import {type UIGameController} from './UIGameController.ts'
import {Point, TradeActions, type TradeCreationData, type TradeResponse} from '@colonist/game-mechanic-validators'
import {type GameState, type NestedReadonly, type PlayerColor, type TradeId} from '@colonist/game-states'
import {UIGameBeginnerModeController} from './ui_game_beginner_mode/UIGameBeginnerModeController.ts'
import {UIGameBeginnerHintsController} from './ui_game_beginner_hints/UIGameBeginnerHintsController.ts'
import {UIUtilsEjsVariables} from '../UIUtilsEjsVariables.ts'
import {ClientUserState} from '../ClientUserState.ts'
import {createGameStore, type GameStore, type GameUserSettingsProps} from '@colonist/ui-game'
import {renderUIGame} from './renderUIGame.tsx'
import {StorageWrapper} from '../StorageWrapper.ts'
import {Sound} from '../Sound.ts'
import {PIXI} from '@colonist/pixi'
import {UIGameMapBottomLogo} from './ui_game_map/UIGameMapBottomLogo.ts'
import ColonistRushLogo from '@static/images/colonist_rush_banner.png'
import ColonistRushGradient from '@static/images/colonist_rush_background_gradient.png'
import {UIGameLargestArmyController} from './ui_game_players/UIGameLargestArmyController.ts'
import {UIGameLongestRoadController} from './ui_game_players/UIGameLongestRoadController.ts'
import {UIGameCityImprovement} from './ui_game_cities_and_knights/UIGameCityImprovement.ts'
import {UIGameKnightController} from './ui_game_cities_and_knights/UIGameKnightController.ts'
import {UIGameFogTileController} from './ui_game_map/UIGameFogTileController.ts'
import {UIGameInventorHighlightDiceController} from './ui_game_map/UIGameInventorHighlightDiceController.ts'
import {UIGameMapChitController} from './ui_game_map/UIGameMapChitController.ts'
import {UIGameMapCityWallController} from './ui_game_map/UIGameMapCityWallController.ts'
import {UIGameMerchantController} from './ui_game_map/UIGameMapMerchantController.ts'
import {UIGameMapPirateController} from './ui_game_map/UIGameMapPirateController.ts'
import {UIGameMapRobberController} from './ui_game_map/UIGameMapRobberController.ts'
import {UIGameMapShipController} from './ui_game_map/UIGameMapShipController.ts'

export interface UIGameManagerMethods {
    canvasController: UIGameCanvasController
    gameController: UIGameController
    socketGameSend: SocketGameSend
    pauseController: UIGamePauseController
    resetGameUIToRebuildOnReconnect: () => void
    gameState: GameClientState
    mapController: UIGameMapController
    eventController: UIGameEventController
}

export abstract class UIGameManager implements UIGameManagerMethods {
    gameController: UIGameController

    gameState: GameClientState
    socketGameSend: SocketGameSend

    eventController: UIGameEventController
    canvasController: UIGameCanvasController
    mapController: UIGameMapController

    actionsController: UIGameActionsController
    toolTipController: UIGameToolTipController
    adController: UIAdController | undefined
    cardController: UIGameCardController

    pauseController: UIGamePauseController

    originalMapPosition: {location: Point, zoom: Point} | undefined

    gameStore: GameStore

    constructor(adController: UIAdController | undefined) {
        this.eventController = new UIGameEventController()

        this.adController = adController
        this.eventController.registerUIGameMechanic(CA)
    }

    abstract openShareLinkMobileModal(link: string): void

    initializeSocket(gameState: GameClientState) {
        this.gameState = gameState
        this.socketGameSend = new SocketGameSend(socket.controller.sender, () => this.gameState.serverId)
    }

    resetGameUIToRebuildOnReconnect() {
        if(!this.canvasController.builtGame()) return
        const mapLocation = new Point(this.mapController.mapView.left, this.mapController.mapView.top)
        const mapZoom = new Point(this.mapController.mapView.scale.x, this.mapController.mapView.scale.y)
        this.originalMapPosition = {
            location: mapLocation,
            zoom: mapZoom,
        }
        this.canvasController.app.stage.removeChildren()
        this.eventController = new UIGameEventController()
    }

    buildGame(configurations: BuildGameConfigurations): void {
        this.gameStore = createGameStore({
            gameGeneral: {
                initialMyColor: this.gameController.myColor,
                initialTimeLeftInSeconds: this.gameController.timeLeftInCurrentState,
            },
            gameState: {
                initialGameState: configurations.gameState,
            },
            gameUserStates: {
                initialUserStates: configurations.playerUserStates,
            },
            gameVote: {
                onVoteAccept: (voteId) => {
                    this.socketGameSend.clickedVote(voteId, true)
                },
                onVoteReject: (voteId) => {
                    this.socketGameSend.clickedVote(voteId, false)
                },
            },
            gameSettings: {
                initialGameSettings: configurations.gameSettings,
            },
            gameMode: {
                initialGameModeData: this.gameState.gameModeData,
            },
            gameDetails: {
                matchmakingMatchType: configurations.gameDetails.matchmakingMatchType,
                isRanked: configurations.gameDetails.isRanked,
                isDiscord: configurations.gameDetails.isDiscord,
                playOrder: configurations.playOrder,
                isCanvasFullWidth: this.canvasController.responsiveController.isCanvasFullWidth,
            },
            gameUserSettings: this.getGameUserSettings(),
            gameTrade: {
                sendUpdateTradeResponse: (tradeResponse: TradeResponse) => {
                    this.socketGameSend.updateTradeResponse(tradeResponse)
                },
                onClickExecuteTrade: (tradeId: TradeId, playerColor: PlayerColor) => {
                    this.socketGameSend.executeTrade(tradeId, playerColor)
                },
                onClickCreateTrade: (tradeCreationData: TradeCreationData) => {
                    this.socketGameSend.createTrade(tradeCreationData)
                    if(tradeCreationData.counterOfferInResponseToTradeId == undefined) return
                    this.socketGameSend.updateTradeResponse({id: tradeCreationData.counterOfferInResponseToTradeId, response: TradeActions.CounterOfferCreatedOrCanceled})
                },
                onClickToggleEmbargoAgainstPlayer: (color: PlayerColor) => {
                    this.socketGameSend.toggleEmbargoAgainstPlayer(color)
                },
                sendCancelAction: () => {
                    this.socketGameSend.cancelAction()
                },
            },
            gamePause: {
                togglePause: () => {
                    this.socketGameSend.togglePause()
                },
            },
            gameReplay: {
                isReplay: configurations.isReplay ?? false,
            },
        })

        renderUIGame({
            gameManager: this,
            options: {
                initialCanResign: this.gameState.canResign,
                isAfterLiveGame: configurations.isAfterLiveGame,
                isChatEnabled: !UIUtilsEjsVariables.isUserOnDiscordMobile,
            },
            gameStore: this.gameStore,
        })
    }

    protected buildOptionalMechanics(gameState: NestedReadonly<GameState>) {
        if(this.gameController.gameSettings.modeSetting == ModeSettingEnum.BeginnerModeClassic4P) {
            this.initBeginnerMode()
            return
        }

        const areBeginnerHintsEnabled = !gameState.mechanicBarbarianInvasionState && !gameState.mechanicPirateState && gameState.experimentalMechanicState?.mechanicTurnColonistRushState == undefined
        if(gameState.mechanicSettlementState
                    && gameState.mechanicCityState
                    && gameState.mechanicRoadState
                    && areBeginnerHintsEnabled) this.initBeginnerHintsController()

        if(gameState.mechanicLongestRoadState) {
            new UIGameLongestRoadController(this, gameState.mechanicLongestRoadState)
        }

        if(gameState.mechanicLargestArmyState) {
            new UIGameLargestArmyController(this, gameState.mechanicLargestArmyState, gameState.mechanicDevelopmentCardsState)
        }

        if(gameState.mechanicCityWallState) {
            new UIGameMapCityWallController(this, gameState.mechanicCityWallState)
        }

        if(gameState.mechanicShipState) {
            new UIGameMapShipController(this, gameState.mechanicShipState, gameState.mechanicPirateState)
        }

        if(gameState.mechanicRobberState) {
            new UIGameMapRobberController(this, gameState.mechanicRobberState)
        }

        if(gameState.mechanicPirateState) {
            new UIGameMapPirateController(this, gameState.mechanicPirateState)
        }

        if(gameState.mechanicIslandExplorationState) {
            new UIGameMapChitController(this)
        }

        if(gameState.mapState.fogTileGroupStates) {
            new UIGameFogTileController(this, gameState.mapState.fogTileGroupStates)
        }

        if(gameState.mechanicKnightState) {
            new UIGameKnightController(this, gameState.mechanicKnightState, gameState.mechanicRobberState, gameState.mechanicPirateState)
        }

        if(gameState.mechanicMerchantState) {
            new UIGameMerchantController(this, gameState.mechanicMerchantState)
        }

        if(gameState.mechanicProgressCardsState) {
            new UIGameInventorHighlightDiceController(this)
        }

        if(gameState.mechanicCityImprovementsState) {
            new UIGameCityImprovement(this, gameState.mechanicCityImprovementsState, gameState.mechanicRobberState)
        }
    }

    protected initBeginnerMode() {
        new UIGameBeginnerModeController(this)
        this.cardController.initBeginnerModeHelper()
    }

    private initBeginnerHintsController() {
        if(ClientUserState.userState != undefined && !ClientUserState.userState.isBeginner()) return
        if(UIUtilsEjsVariables.isUserOnMobileAppOrWeb) return

        new UIGameBeginnerHintsController(this)
    }

    private getGameUserSettings(): GameUserSettingsProps {
        const isAntialiasEnabledDefault = window.devicePixelRatio * window.innerWidth <= 2000
        const resolutionDefault = (() => {
            const allowedResolutions = [0.5, 1, 1.5, 2]
            const rawResolution = UIUtilsEjsVariables.isUserOnDiscord ? 2 : window.devicePixelRatio
            return allowedResolutions.reduce((prev, curr) => curr <= rawResolution ? curr : prev, 0.5)
        })()

        return {
            initialVolume: StorageWrapper.getMasterVolume() ?? Sound.DEFAULT_VOLUME,
            initialIsMuted: StorageWrapper.getIsMuted(),
            initialIsAnimationsEnabled: StorageWrapper.getAnimation() ?? true,
            initialIsAntialiasEnabled: StorageWrapper.getAntialias() ?? isAntialiasEnabledDefault,
            initialIsKeyboardShortcutsEnabled: StorageWrapper.getKeyboardShortcuts() ?? true,
            initialResolution: StorageWrapper.getResolution() ?? resolutionDefault,
            initialPowerPreference: StorageWrapper.getPowerPreference(),
            setCanvasOptions: (options: Partial<PIXI.IApplicationOptions>) => {
                this.canvasController.updateCanvasSettings(options)
            },
        }
    }

    protected addColonistRushBranding() {
        const gradientTexture = PIXI.Texture.from(ColonistRushGradient)
        this.canvasController.setBackgroundSprite(gradientTexture)

        const logoTexture = PIXI.Texture.from(ColonistRushLogo, {
            width: 288,
            height: 97,
        })
        new UIGameMapBottomLogo(this.mapController, logoTexture)
    }
}
